import { useEffect, useState } from "react";
import PortalNav from "../components/PortalNav";
import { api, formatDetail } from "../lib/api";
import { toast, Toaster } from "sonner";

const MOODS = [
  { v: 1, l: "Heavy" },
  { v: 2, l: "Foggy" },
  { v: 3, l: "Neutral" },
  { v: 4, l: "Soft" },
  { v: 5, l: "Radiant" },
];

export default function WellnessLog() {
  const [mood, setMood] = useState(4);
  const [energy, setEnergy] = useState(3);
  const [water, setWater] = useState(64);
  const [sleep, setSleep] = useState(8);
  const [notes, setNotes] = useState("");
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/wellness");
      setEntries(data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/wellness", { mood, energy, water_oz: Number(water), sleep_hrs: Number(sleep), notes });
      setNotes("");
      toast.success("Logged. Softly done.");
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="portal-body" data-testid="wellness-log-page">
      <Toaster position="top-center" />
      <header style={{ background: "var(--charcoal)", color: "var(--ivory)", padding: "28px 24px 32px" }}>
        <div className="container-x">
          <p className="eyebrow eyebrow-light">Wellness Log</p>
          <h1 style={{ fontFamily: "var(--serif)", fontWeight: 300, fontSize: "clamp(1.8rem, 4vw, 2.4rem)", margin: "10px 0" }}>Today's soft check-in.</h1>
          <p style={{ color: "rgba(250,246,238,.55)", fontSize: ".95rem" }}>Notice yourself. There's no wrong answer.</p>
        </div>
      </header>

      <main className="container-x" style={{ padding: "32px 24px" }}>
        <form onSubmit={submit} className="portal-card" style={{ padding: 32, marginBottom: 32 }} data-testid="wellness-form">
          {/* Mood */}
          <div style={{ marginBottom: 32 }}>
            <p className="eyebrow" style={{ marginBottom: 16 }}>Mood</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8 }}>
              {MOODS.map((m) => (
                <button key={m.v} type="button" onClick={() => setMood(m.v)}
                  className="btn"
                  style={{ padding: "18px 8px", background: mood === m.v ? "var(--gold)" : "transparent", color: mood === m.v ? "var(--ivory)" : "var(--charcoal)", border: `1px solid ${mood === m.v ? "var(--gold)" : "rgba(184,150,90,.3)"}`, display: "flex", flexDirection: "column", gap: 6 }}
                  data-testid={`mood-${m.v}`}
                >
                  <span style={{ fontFamily: "var(--serif)", fontSize: "1.3rem" }}>{m.v}</span>
                  <span style={{ fontSize: ".62rem", letterSpacing: ".2em" }}>{m.l}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Energy */}
          <div style={{ marginBottom: 32 }}>
            <p className="eyebrow" style={{ marginBottom: 16 }}>Energy — {energy}</p>
            <input type="range" min="1" max="5" value={energy} onChange={(e) => setEnergy(+e.target.value)} style={{ width: "100%", accentColor: "var(--gold)" }} data-testid="energy-slider" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ color: "var(--warm-gray)" }}>Water (oz)</label>
              <input type="number" value={water} onChange={(e) => setWater(e.target.value)} style={{ color: "var(--charcoal)", borderBottomColor: "rgba(184,150,90,.3)" }} data-testid="water-input" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ color: "var(--warm-gray)" }}>Sleep (hrs)</label>
              <input type="number" step="0.5" value={sleep} onChange={(e) => setSleep(e.target.value)} style={{ color: "var(--charcoal)", borderBottomColor: "rgba(184,150,90,.3)" }} data-testid="sleep-input" />
            </div>
          </div>

          <div className="form-group">
            <label style={{ color: "var(--warm-gray)" }}>Notes</label>
            <textarea placeholder="What's rising in you today?" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ color: "var(--charcoal)", borderBottomColor: "rgba(184,150,90,.3)" }} data-testid="notes-input" />
          </div>

          <button type="submit" disabled={busy} className="btn btn-primary" style={{ width: "100%" }} data-testid="wellness-submit">
            {busy ? "Saving…" : "Log today"}
          </button>
        </form>

        {/* History */}
        <p className="eyebrow" style={{ marginBottom: 16 }}>Recent Check-ins</p>
        {entries.length === 0 ? (
          <p className="body-sm">No check-ins yet. Your first is a beginning.</p>
        ) : (
          <div style={{ display: "grid", gap: 12 }} data-testid="log-history">
            {entries.slice(0, 7).map((e) => (
              <div key={e.id} className="portal-card" style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr auto", gap: 16 }}>
                <div>
                  <p style={{ fontFamily: "var(--serif)", fontSize: "1.1rem", margin: "0 0 6px" }}>{new Date(e.created_at).toLocaleString(undefined, { weekday: "long", month: "short", day: "numeric" })}</p>
                  {e.notes && <p className="body-sm" style={{ margin: 0 }}>{e.notes}</p>}
                </div>
                <div style={{ display: "flex", gap: 16, alignItems: "center", fontSize: ".78rem", color: "var(--warm-gray)", letterSpacing: ".1em" }}>
                  <span>Mood {e.mood}</span>
                  <span>Energy {e.energy}</span>
                  <span>{e.sleep_hrs}h</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <PortalNav />
    </div>
  );
}
